import { useState } from 'react'
import { useDispatch } from 'react-redux'

import ToggleButton from 'common/ToggleButton/ToggleButton'
import * as Actions from 'redux/actions'
import { Workspace } from 'types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './SecuritySettings.module.scss'

interface WorkspaceHandbookFormProps {
  workspace: Workspace
  workspaces: Workspace[]
}

export const WorkspaceHandbookForm = ({ workspace, workspaces }: WorkspaceHandbookFormProps) => {
  const [hideInaccessibleHandboooks, setHideInaccessibleHandboooks] = useState(!workspace.show_all_handbooks)
  const [readMode, setReadMode] = useState(workspace.handbook_default_read_mode)
  const dispatch = useDispatch()

  const toggleShowAllHandbooks = () => {
    const update = async () => {
      const res = await client.updateWorkspaceSettings(
        {
          workspace: {
            show_all_handbooks: hideInaccessibleHandboooks,
          },
        },
        workspace.key,
      )

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage('Your workspace has been updated!', 'success')
        dispatch(
          Actions.workspacesSet(
            workspaces.map((w) =>
              w.key === workspace.key ? { ...w, show_all_handbooks: hideInaccessibleHandboooks } : w,
            ),
          ),
        )
        setHideInaccessibleHandboooks(!hideInaccessibleHandboooks)
      } else {
        showToastMessage("Couldn't update your workspace settings, please try again later", 'error')
      }
    }
    update()
  }

  const toggleHandbookReadMode = () => {
    const update = async () => {
      const res = await client.updateWorkspaceSettings(
        {
          workspace: {
            handbook_default_read_mode: !readMode,
          },
        },
        workspace.key,
      )

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage('Your workspace has been updated!', 'success')
        dispatch(
          Actions.workspacesSet(
            workspaces.map((w) => (w.key === workspace.key ? { ...w, handbook_default_read_mode: !readMode } : w)),
          ),
        )
        setReadMode(!readMode)
      } else {
        showToastMessage("Couldn't update your workspace settings, please try again later", 'error')
      }
    }
    update()
  }

  return (
    <div className={Styles.settingsContent}>
      <div className={Styles.settingsOptions}>
        <div className={Styles.settingsOption}>
          <div className={Styles.toggleRow}>
            <div className={Styles.optionHeader}>Only allow members to see handbook titles they have access to</div>
            <ToggleButton
              name="showAllHandbooks"
              checked={hideInaccessibleHandboooks}
              handleChange={toggleShowAllHandbooks}
            ></ToggleButton>
          </div>
          <div className={Styles.helpText}>
            This will prevent users from seeing all handbooks in a workspace on the handbook left rail. Toggle this off
            if you want handbook titles to be self-discoverable by workspace members.
          </div>
        </div>
        <div className={Styles.settingsOption}>
          <div className={Styles.toggleRow}>
            <div className={Styles.optionHeader}>Make handbook documents read-only by default</div>
            <ToggleButton
              name="handbookReadMode"
              checked={readMode}
              handleChange={toggleHandbookReadMode}
            ></ToggleButton>
          </div>
          <div className={Styles.helpText}>
            Show all docs shared in a handbook as read-only for all users. Users with higher accesses can switch to edit
            or comment view after the doc is open
          </div>
        </div>
      </div>
    </div>
  )
}
